function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import hasIn from 'lodash/hasIn';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import set from 'lodash/set';
import unset from 'lodash/unset';
import startsWith from 'lodash/startsWith';
import endsWith from 'lodash/endsWith';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isFinite from 'lodash/isFinite';
import isInteger from 'lodash/isInteger';
import isNan from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isObjectLike from 'lodash/isObjectLike';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import join from 'lodash/join';
import split from 'lodash/split';
import padStart from 'lodash/padStart';
import toNumber from 'lodash/toNumber';
import toString from 'lodash/toString';
import XRegExp from 'xregexp';
import moment from 'moment';
const precision = (a)=>{
    if (!isFinite(a)) return 0;
    let e = 1;
    let p = 0;
    while(Math.round(a * e) / e !== a){
        e *= 10;
        p++;
    }
    return p;
};
const convertToNumber = (a)=>{
    if (isString(a)) {
        a = a.trim().replace(',', '.');
    }
    return toNumber(a);
};
const splitNoParen = (s)=>{
    const M = s.match(/([^()]+)|([()])/g);
    const L = M.length;
    let A = [];
    let left = 0;
    let right = 0;
    let next;
    let str = '';
    for(let i = 0; i < L; i++){
        next = M[i];
        if (next === '(') {
            ++left;
        } else if (next === ')') {
            ++right;
        }
        if (left !== 0) {
            str += next;
            if (left === right) {
                A[A.length - 1] += str.slice(1, -1);
                left = 0;
                right = 0;
                str = '';
            }
        } else {
            A = A.concat(next.match(/([^,]+)/g));
        }
    }
    return A;
};
// Allow nested object as a form value
const checkValues = (validatorsRules, validatedValues, subValues = null, path = '')=>{
    const values = subValues || validatedValues;
    forEach(values, (value, key)=>{
        if (isObject(value) && !isArray(value)) {
            checkValues(validatorsRules, validatedValues, value, key);
            if (isEmpty(value)) unset(validatedValues, key);
            return;
        }
        const fullKey = path ? `${path}.${key}` : key;
        if (!hasIn(validatorsRules, fullKey)) {
            unset(validatedValues, fullKey);
        }
    });
};
class Validator {
    static run(values = {}, validatorsRules = {}, stopOnFirstError = true) {
        let errors = {};
        const validatedValues = _object_spread({}, values);
        if (!isEmpty(validatedValues)) {
            checkValues(validatorsRules, validatedValues);
        }
        forEach(validatorsRules, (validatorRules, dataFieldName)=>{
            validatorRules = splitNoParen(validatorRules);
            forEach(validatorRules, (validatorRule)=>{
                validatorRule = validatorRule.trim();
                if (validatorRule === '*') {
                    return;
                }
                const validatorRuleParameters = validatorRule.split(' ');
                validatorRule = validatorRuleParameters.shift();
                let validationResult = {};
                if (typeof this[validatorRule] === 'function') {
                    validationResult = this[validatorRule](get(validatedValues, dataFieldName), validatorRuleParameters, validatedValues, validatorsRules);
                    if (isUndefined(validationResult.value)) delete validatedValues[dataFieldName];
                    else validatedValues[dataFieldName] = validationResult.value;
                } else {
                    validationResult = {
                        valid: false
                    };
                }
                if (!validationResult.valid) {
                    set(errors, [
                        dataFieldName,
                        validatorRule
                    ], validatorRuleParameters);
                    if (stopOnFirstError) {
                        return false;
                    }
                }
            });
        });
        if (isEmpty(errors)) {
            errors = null;
        }
        return {
            validatedValues,
            errors
        };
    }
    /**
   * Diagnostic, ALWAYS returns error
   */ static forceError(value) {
        return {
            value,
            valid: false
        };
    }
    /**
   * Trimming bypass
   */ static trim(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        return {
            value,
            valid: true
        };
    }
    /**
   * Non-false value is required
   */ static required(value = '') {
        const delimiter = '\u200E';
        // eslint-disable-next-line no-shadow
        const convertValueToString = (input, delimiter)=>{
            if (!input && input !== 0) {
                return '';
            }
            let output = input;
            if (isObjectLike(input)) {
                output = [];
                forEach(input, (val)=>{
                    output.push(convertValueToString(val, delimiter));
                });
                output = output.join(delimiter);
            } else if (!isString(input)) {
                output = toString(input);
            }
            return output.trim();
        };
        if (isString(value)) {
            value = value.trim();
        }
        const convertedValue = convertValueToString(value, delimiter);
        return {
            value,
            valid: convertedValue && !startsWith(convertedValue, delimiter) && !endsWith(convertedValue, delimiter) && !includes(convertedValue, delimiter + delimiter)
        };
    }
    /**
   * Required length of a string / an array.
   */ static length(value, validatorRuleParameters = []) {
        if (!isString(value) && !isArray(value)) {
            value = toString(value);
        }
        if (isString(value)) {
            value = value.trim();
        }
        return {
            value,
            valid: value.length === +validatorRuleParameters[0]
        };
    }
    /**
   * Max length of a string / an array.
   */ static limit(value, validatorRuleParameters = []) {
        if (!isString(value) && !isArray(value)) {
            value = toString(value);
        }
        if (isString(value)) {
            value = value.trim();
        }
        return {
            value,
            valid: value.length <= validatorRuleParameters[0]
        };
    }
    /**
   * Max length of a string / an array. Same as limit but with different errorMessage
   */ static limitDigits(value, validatorRuleParameters = []) {
        if (!isString(value) && !isArray(value)) {
            value = toString(value);
        }
        if (isString(value)) {
            value = value.trim();
        }
        return {
            value,
            valid: value.length <= validatorRuleParameters[0]
        };
    }
    /**
   * Min length of a string / an array. Empty is also valid (use together with 'required' to prevent this).
   */ static minimum(value, validatorRuleParameters = []) {
        if (!isString(value) && !isArray(value)) {
            value = toString(value);
        }
        if (isString(value)) {
            value = value.trim();
        }
        return {
            value,
            valid: !value || value.length >= validatorRuleParameters[0]
        };
    }
    /**
   * Range of valid length described by two parameters.
   */ static range(value, validatorRuleParameters = []) {
        if (!isString(value) && !isArray(value)) {
            value = toString(value);
        }
        if (isString(value)) {
            value = value.trim();
        }
        const valid = !value || value.length >= validatorRuleParameters[0] && value.length <= validatorRuleParameters[1];
        return {
            value,
            valid
        };
    }
    /**
   * Allows for letters, digits, spaces and dashes only
   */ static alphaNumeric(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        return {
            value,
            valid: value === '' || !value.match(/[^\w -]/)
        };
    }
    /**
   * Allows for capital Letters only
   */ static capitalLetters(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        return {
            value,
            valid: value === '' || value.match(/^[A-Z]+$/)
        };
    }
    /**
   * Allows for lowerLetters only
   */ static lowerLetters(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        return {
            value,
            valid: value === '' || value.match(/^[a-z]+$/)
        };
    }
    /**
   * Allows for letters dashes and underscore only
   */ static capitalLettersDashesAndUnderscore(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        return {
            value,
            valid: value === '' || value.match(/^[A-Z_-]+$/)
        };
    }
    /**
   * allows only 0/1 values (as strings)
   */ static bit(value = '0') {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        if (value === '') {
            value = '0';
        }
        return {
            value,
            valid: value === (value & 1).toString()
        };
    }
    /**
   * allows only false/true values
   */ static bool(value = false) {
        let isTrue;
        if (isBoolean(value)) {
            isTrue = value;
        } else if (isString(value)) {
            value = value.trim();
            isTrue = !(!value || value === '0' || value === 'false');
        } else if (isNumber(value)) {
            isTrue = value !== 0;
        } else {
            // Arrays, objects
            isTrue = !isEmpty(value);
        }
        return {
            value: isTrue,
            valid: true
        };
    }
    /**
   * allows only true values
   */ static boolTrue(value = false) {
        let isTrue;
        if (isBoolean(value)) {
            isTrue = value;
        } else if (isString(value)) {
            value = value.trim();
            isTrue = !(!value || value === '0' || value === 'false');
        } else if (isNumber(value)) {
            isTrue = value !== 0;
        } else {
            // Arrays, objects
            isTrue = !isEmpty(value);
        }
        return {
            value: isTrue,
            valid: isTrue
        };
    }
    // NUMBER VALUES ---------------------------------------------------
    /**
   * Allows for integer value only
   */ static integer(value = '') {
        if (value === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = toNumber(value);
        }
        return {
            value,
            valid: isInteger(value)
        };
    }
    /**
   * Allows for decimal number value only
   */ static decimal(value = '') {
        if (value === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = convertToNumber(value);
        }
        return {
            value,
            valid: isFinite(value)
        };
    }
    /**
   * Allows for double precision decimal number value only
   */ static doublePrecision(value = '') {
        if (value === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = convertToNumber(value);
        }
        return {
            value,
            valid: isFinite(value) && precision(value) <= 2
        };
    }
    /**
   * Allows for value greater than 0 only
   */ static positive(value = '') {
        if (value === '' || value === null) {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = convertToNumber(value);
        }
        return {
            value,
            valid: isFinite(value) && value > 0
        };
    }
    /**
   * Allows for value greater or equal to 0 only
   */ static nonNegative(value = '') {
        if (value === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = convertToNumber(value);
        }
        return {
            value,
            valid: isFinite(value) && value >= 0
        };
    }
    /**
   * Allows for value lower or equal to specified value
   */ static maxValue(value = '', validatorRuleParameters = []) {
        if (value === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = convertToNumber(value);
        }
        return {
            value,
            valid: isFinite(value) && value <= validatorRuleParameters[0]
        };
    }
    /**
   * Allows for value greater or equal to specified value
   */ static minValue(value = '', validatorRuleParameters = []) {
        if (value === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = convertToNumber(value);
        }
        return {
            value,
            valid: isFinite(value) && value >= validatorRuleParameters[0]
        };
    }
    /**
   * Allows for value greater or equal to value from specified formValue
   */ static greaterThan(value = '', validatorRuleParameters = [], values = {}) {
        if (value === '' || value === null) {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = convertToNumber(value);
        }
        let greaterThanValue = values[validatorRuleParameters[0]];
        if (greaterThanValue === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(greaterThanValue)) {
            greaterThanValue = convertToNumber(greaterThanValue);
        }
        if (validatorRuleParameters.length === 1) {
            validatorRuleParameters[1] = greaterThanValue;
        }
        return {
            value,
            valid: isFinite(value) && isFinite(greaterThanValue) && value > greaterThanValue
        };
    }
    /**
   * Allows for value lower or equal to value from specified formValue
   */ static lowerThan(value = '', validatorRuleParameters = [], values = {}) {
        if (value === '' || value === null) {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = convertToNumber(value);
        }
        let lowerThanValue = values[validatorRuleParameters[0]];
        if (lowerThanValue === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(lowerThanValue)) {
            lowerThanValue = convertToNumber(lowerThanValue);
        }
        if (validatorRuleParameters.length === 1) {
            validatorRuleParameters[1] = lowerThanValue;
        }
        return {
            value,
            valid: isFinite(value) && isFinite(lowerThanValue) && value < lowerThanValue
        };
    }
    // REG EXP --------------------------------------------------------
    static regExp(value, validatorRuleParameters = []) {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        const regExp = new RegExp(validatorRuleParameters[0]);
        return {
            value,
            valid: !value || regExp.test(value)
        };
    }
    static regExpSyntax(value) {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        if (!value) {
            return {
                value,
                valid: true
            };
        }
        try {
            const regExp = new RegExp(value);
            regExp.test('-');
        } catch (err) {
            return {
                value,
                valid: false
            };
        }
        return {
            value,
            valid: value.indexOf(' ') < 0
        };
    }
    // COMPARATORS ----------------------------------------------------
    static equalTo(value, validatorRuleParameters, values) {
        return {
            value,
            valid: !value || value === values[validatorRuleParameters[0]]
        };
    }
    static masterFor(value, validatorRuleParameters, values) {
        let valid = true;
        const slaveName = validatorRuleParameters[0];
        const slaveValue = get(values, slaveName);
        if (!value) {
            if (slaveValue) {
                valid = false;
            }
        }
        return {
            value: value && slaveValue ? value : undefined,
            valid
        };
    }
    static slaveFor(value, validatorRuleParameters, values) {
        const masterName = validatorRuleParameters[0];
        const masterValue = get(values, masterName);
        return {
            value: value && masterValue ? value : undefined,
            valid: true
        };
    }
    // DATA VALUES ----------------------------------------------------
    /**
   * Non-false value of one of two fields is required
   */ static alternative(value, validatorRuleParameters, data) {
        if (!isString(value)) {
            value = toString(value);
        }
        return {
            value,
            valid: value || get(data, validatorRuleParameters[0], false)
        };
    }
    /**
   * Allows for proper email address only
   */ static email(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim().toLowerCase();
        return {
            value,
            valid: !value || value.match(/^([_a-z0-9-]+)([.+][_a-z0-9-]+)*@([a-z0-9-]+)(\.[a-z0-9-]+)*(\.[a-z]{2,})$/)
        };
    }
    /**
   * Allows for YYYY-MM-DD date format only
   */ static date(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        return {
            value,
            valid: !value || moment(value, 'YYYY-MM-DD', true).isValid()
        };
    }
    /**
   * Allows for H:mm time format only
   */ static time(value = '') {
        if (!value) {
            return {
                value: '',
                valid: true
            };
        }
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        const hour = split(value, ':');
        if (hour.length === 1) {
            hour.push(0);
        }
        const time = [];
        forEach(hour, (t, k)=>{
            const v = toNumber(t);
            if (!isNan(v) && v >= 0 && (!k && v < 24 || k && v < 60)) {
                time.push(padStart(v, 2, 0));
            }
        });
        if (time.length !== 2) {
            return {
                value,
                valid: false
            };
        }
        return {
            value: join(time, ':'),
            valid: true
        };
    }
    /**
   * Allows for timestamp format
   */ static timestamp(value = '') {
        if (value === '') {
            return {
                value: null,
                valid: true
            };
        }
        if (!isNumber(value)) {
            value = toNumber(value);
        }
        return {
            value,
            valid: isInteger(value) && value >= 0
        };
    }
    /**
   * Allows for proper uuid v4 only
   */ static uuid(uuid) {
        const r = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        return isString(uuid) && r.test(uuid);
    }
    /**
   * Allows for proper phone number only
   */ static phone(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        return {
            value,
            valid: !value || value.match(/^\+\d{2}\s\d{3}\s\d{3}\s\d{3}$/)
        };
    }
    /**
   * Allows for proper phone with prefix
   */ static phoneWithPrefix(value) {
        const prefix = get(value, 'prefix', '').replace(/s/g, '');
        const number = get(value, 'number', '').replace(/s/g, '');
        return {
            value: {
                prefix,
                number
            },
            valid: (!prefix || prefix.match(/^\+\d{1,3}-?\d{0,4}$/)) && (!number || number.match(/^[\d\s-().]{5,}$/))
        };
    }
    /**
   * Allows for proper zipcode only
   */ static zipcode(value = '') {
        if (!isString(value)) {
            value = toString(value);
        }
        value = value.trim();
        return {
            value,
            valid: !value || value.length >= 4 && value.length <= 15
        };
    }
    /**
   * The password must contain at least eight characters, one number and one letter.
   */ static password(value) {
        if (!isString(value)) {
            value = toString(value);
        }
        const regExp = new XRegExp('^((?=.*\\pN)(?=.*\\pL)(.*))$');
        return {
            value,
            valid: !value || value.length >= 8 && regExp.test(value)
        };
    }
    /**
   * Allows for url format   */ static url(value) {
        if (value === '') {
            return {
                value,
                valid: true
            };
        }
        let valid;
        try {
            valid = Boolean(new URL(value));
        } catch (e) {
            valid = false;
        }
        return {
            value,
            valid
        };
    }
    /**
   * The password must contain at least eight characters
   */ static passwordMinimum(value) {
        if (!isString(value)) {
            value = toString(value);
        }
        return {
            value,
            valid: !value || value.length >= 8
        };
    }
    /**
   * The password must contain at least one number
   */ static atLeastOneNumber(value) {
        if (!isString(value)) {
            value = toString(value);
        }
        return {
            value,
            valid: /\d/.test(value)
        };
    }
    /**
   * The password must contain at least one uppercase letter
   */ static atLeastOneUppercase(value) {
        if (!isString(value)) {
            value = toString(value);
        }
        return {
            value,
            valid: /[A-Z]/.test(value)
        };
    }
    /**
   * The password must contain at least one special character
   */ static atLeastSpecialCharacter(value) {
        if (!isString(value)) {
            value = toString(value);
        }
        return {
            value,
            valid: /[!@#$%^&*()_\-+=[\]{};:'"\\|,.<>/?]/.test(value)
        };
    }
}
/* eslint-disable consistent-return */ /**
 * Function to filter and validate data sets.
 * @param {object} data to validate
 * @param {object} validatorsRules rules as strings separated by coma. `*` is a passthrough wildcard.
 * @return @return {object} validated data and errors object
 */ export { Validator as default };
